/**
 * The MD-03 panel-5 conversion arithmetic (owner ruling R6), client copy of
 * the server's exact math: quantity × factor on the DECIMAL STRINGS with
 * BigInt — never a float — rounded half-up ONCE to the stock unit's
 * precision (GEN-101). Used by the conversion editor to read a row back as
 * "1 Box = 24 Pieces" and to preview what a quantity becomes.
 */

/** Exact qty × factor, rounded half-up to `precision` decimals. */
export function multiplyQtyByFactor(qty: string, factor: string, precision: number): string {
  const [qi = '0', qf = ''] = qty.split('.');
  const [fi = '0', ff = ''] = factor.split('.');
  const scale = qf.length + ff.length;
  const product = BigInt(qi + qf) * BigInt(fi + ff);
  if (scale <= precision) {
    return formatScaled(product * 10n ** BigInt(precision - scale), precision);
  }
  const divisor = 10n ** BigInt(scale - precision);
  const rounded = (product + divisor / 2n) / divisor;
  return formatScaled(rounded, precision);
}

function formatScaled(value: bigint, scale: number): string {
  const digits = value.toString().padStart(scale + 1, '0');
  return scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
}

/** "24.000000" → "24" — NUMERIC scale padding is noise on screen. */
export function trimZeros(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '');
}

/** Decimal places genuinely carried (trailing zeros ignored). */
export function decimalsOf(value: string): number {
  const fraction = value.split('.')[1] ?? '';
  return fraction.replace(/0+$/, '').length;
}

/** A well-formed factor: digits, up to 6 decimals, above zero (ERR-MD-03-10). */
export function isValidFactor(value: string): boolean {
  return /^\d{1,12}(\.\d{1,6})?$/.test(value) && !/^0+(\.0+)?$/.test(value);
}
